import { Link } from "react-router-dom";
import { Rating } from "@/components/ui/rating";
import AddToBasket from "@/components/shop/AddToBasket";
import { useCatalog, bundleSaving, pricePerLitre, isSoldOut } from "@/lib/shop";
import { trackSelectItem } from "@/lib/analytics";
import { realGallery } from "@/lib/productImage";
import BottleArt from "@/components/visual/BottleArt";

/**
 * ProductCard: add to basket puts the first (smallest) variant in the cart.
 */
export default function ProductCard({ product, index = 0, listName = "Products" }) {
  const photo = realGallery(product)[0];
  const lowest = product.price_range?.min ?? 0;
  const highest = product.price_range?.max ?? 0;
  const firstVariant = product.variants?.[0];
  const catalog = useCatalog();
  const saving = firstVariant ? bundleSaving(firstVariant, catalog) : null;
  // Per litre only helps where there is a bigger jar to compare against.
  const ppl = (product.variants?.length || 0) > 1 ? Math.min(...product.variants.map((v) => pricePerLitre(v) || Infinity)) : null;

  return (
    <Link
      to={`/products/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      onClick={() => trackSelectItem(listName, product, index)}
      className="group rounded-3xl bg-white overflow-hidden flex flex-col transition-shadow hover:shadow-[0_24px_48px_-24px_rgba(0,0,0,0.25)]"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-brand-obsidian flex items-center justify-center">
        {photo ? (
          <img src={photo} alt={product.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
        ) : (
          <BottleArt product={product} />
        )}
        {product.kind && product.kind !== "bottle" && (
          <span className="absolute top-4 right-4 rounded-control bg-brand-gold px-3 py-1 font-body text-[12px] font-semibold text-white">{product.kind === "gift" ? "Gift" : "Family pack"}</span>
        )}
        {isSoldOut(product) && (
          <span className="absolute bottom-4 left-4 rounded-control bg-[#F3E3A1] px-3 py-1 font-body text-[12px] font-semibold text-brand-ink">Sold out · Pre-order open</span>
        )}
        {product.is_new_launch && (
          <span className="absolute top-4 left-4 rounded-control bg-white/90 px-3 py-1 font-body text-[12px] font-semibold text-brand-ink">New</span>
        )}
      </div>

      <div className="p-7 flex-1 flex flex-col">
        <h3 className="font-display text-[22px] leading-tight tracking-[-0.02em] text-brand-ink">{product.name}</h3>
        {product.review_count > 0 && (
          <div className="flex items-center gap-2 mt-2">
            <Rating value={product.avg_rating} size={14} />
            <span className="font-body text-xs text-ink-muted">
              {product.avg_rating?.toFixed(1)} from {product.review_count.toLocaleString()} {product.review_count === 1 ? "review" : "reviews"}
            </span>
          </div>
        )}
        <p className="font-body text-[15px] text-ink-muted mt-2 line-clamp-2 flex-1">{product.short_desc}</p>
        <div className="mt-6 flex items-end justify-between gap-3">
          <div>
            <p className="font-body text-[15px] text-brand-ink">
              {highest !== lowest ? <>From ₹{lowest}</> : <>₹{lowest}</>}
              {firstVariant && highest === lowest && <span className="text-ink-muted"> · {firstVariant.size}</span>}
            </p>
            {saving ? (
              <p className="font-body text-[12.5px] font-semibold text-brand-gold">Save ₹{saving.saving} against single jars</p>
            ) : ppl && Number.isFinite(ppl) ? (
              <p className="font-body text-[12.5px] text-ink-muted">As low as ₹{ppl} a litre</p>
            ) : null}
          </div>
          <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
            <AddToBasket product={product} variant={firstVariant} label="Add" className="h-10 px-5 text-[14px]" testid={`product-card-quickadd-${product.slug}`} />
          </span>
        </div>
      </div>
    </Link>
  );
}
